import React, {useCallback, useRef, useState} from 'react';

import {CommonInputInterface} from '../common-input-interface';
import {useFocus} from '../input-hooks';
import {Field} from '../field';
import {ChevronDownIcon, ChevronUpIcon, CrossIcon} from '../../svg-icon';
import {cls, isMultiple, precision, useUniqueId} from '../../../utils';
import {Button} from '../button';

import inputCss from '../input/input.module.css';
import css from './input-number.module.css';
import fieldCss from '../field/field.module.css';

interface InputInterface extends CommonInputInterface<number | null> {
	setRef?: React.RefObject<HTMLInputElement | null>;
	value: number | null;
}

export type InputProps = InputInterface & Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof InputInterface>;

export const InputNumber: React.FC<InputProps> = (props) => {
	const {
		error: propsError,
		disabled,
		className,
		helperText,
		appearance,
		size,
		shape,
		bottomText,
		label,
		disableClear,
		onChange: _,
		setRef,
		...inputProps
	} = props;
	const {hasFocus, onBlur, onFocus} = useFocus(props);
	const {value, onChange} = props;
	const [fieldValue, setFieldValue] = useState<string>();
	const ref = useRef<HTMLInputElement>(null);
	const max = (inputProps.max as number) ?? Infinity;
	const min = (inputProps.min as number) ?? -Infinity;
	const step = inputProps.step === 'any' ? null : (inputProps.step as number) ?? null;
	const [customError, setCustomError] = useState<string>();

	const handleIncrement = useCallback(() => {
		if (props.disabled || props.readOnly) return;
		const currentValue = parseFloat((value || '').toString()) || (inputProps.min as number) || 0;
		const fixedLen = Math.max(precision(currentValue), step ? precision(step) : 0);
		const newValue = parseFloat((currentValue + (step || 1)).toFixed(fixedLen));
		onChange(newValue <= max ? newValue : max);
	}, [onChange, value, max, step, inputProps?.min, props.disabled, props.readOnly]);

	const handleDecrement = useCallback(() => {
		if (props.disabled || props.readOnly) return;
		const currentValue = parseFloat((value || '').toString()) || (inputProps.min as number) || 0;
		const fixedLen = Math.max(precision(currentValue), step ? precision(step) : 0);
		const newValue = parseFloat((currentValue - (step || 1)).toFixed(fixedLen));
		onChange(newValue >= min ? newValue : min);
	}, [onChange, value, min, step, inputProps?.min, props.disabled, props.readOnly]);

	const handleScroll = useCallback(
		(event: any) => {
			if (hasFocus) event.deltaY > 0 ? handleDecrement() : handleIncrement();
		},
		[handleDecrement, handleIncrement, hasFocus],
	);

	const handleValueChange = useCallback(
		(field: string) => {
			if (props.disabled || props.readOnly) return;
			const number = parseFloat(field);
			if (number.toString() === field && (number < min || number > max)) {
				setFieldValue(field);
				setCustomError(undefined);
			} else if (number.toString() === field) {
				if (step && !isMultiple(number, step)) setCustomError(`Число должно быть кратно ${step}`);
				else setCustomError(undefined);
				onChange(number);
				setFieldValue(undefined);
			} else {
				setFieldValue(field);
				setCustomError(undefined);
			}
		},
		[min, max, step, props.disabled, props.readOnly, setCustomError, onChange, setFieldValue],
	);

	const handleFieldChange = useCallback(
		(event: any) => {
			const value = event?.target?.value || '';
			if (value === '') {
				onChange(null);
				setFieldValue(undefined);
				return;
			}
			let afterDotCount = 0;
			let hasDot = false;
			const floatLen = step ? precision(step) : 0;
			const field = [...value]
				.map((current, index, arr) => {
					if (step && hasDot && afterDotCount > floatLen) return '';

					if (index === 0 && current === '-') return '-';
					const prev = parseInt(arr[index - 1], 10);
					const currentNumber = parseInt(current, 10);
					if (current === ',' || current === '.') {
						if (Number.isFinite(prev) && !hasDot) {
							hasDot = true;
							return !step || floatLen > 0 ? '.' : '';
						} else return '';
					}
					if (isFinite(currentNumber)) {
						afterDotCount++;
						return current;
					}
					return '';
				})
				.join('');
			handleValueChange(field);
		},
		[onChange, step, handleValueChange, setFieldValue],
	);

	const handleBlur = useCallback(
		(e: any) => {
			if (fieldValue && ['.', '-'].includes(fieldValue[fieldValue.length - 1])) {
				const field = fieldValue.substring(0, fieldValue.length - 1);
				handleValueChange(field);
			} else if (fieldValue && Number.isFinite(parseFloat(fieldValue))) {
				const number = parseFloat(fieldValue);
				onChange(number < min ? min : number > max ? max : number);
				setFieldValue(undefined);
			}

			if (onBlur) onBlur(e);
		},
		[fieldValue, value, min, max, onChange, onBlur, handleValueChange],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.keyCode === 38) {
				handleIncrement();
				e.preventDefault();
			} else if (e.keyCode === 40) {
				handleDecrement();
				e.preventDefault();
			}
		},
		[handleIncrement, handleDecrement],
	);

	const handleClearValue = useCallback(() => {
		onChange(null);
	}, []);

	const generatedId = useUniqueId('Input');
	const id = props.id || generatedId;
	const error = customError || propsError;
	const hasError = error && error !== true;

	return (
		<Field
			commonProps={{...props, error}}
			hasFocus={hasFocus}
			appearance={appearance ?? 'outline'}
			size={size}
			shape={shape}
			inputId={id}
			buttons={
				<>
					{value?.toString() && !disabled && !disableClear && (
						<Button
							appearance="subtle"
							icon={<CrossIcon width={14} height={14} />}
							onClick={handleClearValue}
							className={fieldCss.actionButton}
						/>
					)}
					<Button
						appearance="subtle"
						icon={<ChevronUpIcon width={14} height={14} />}
						onClick={handleIncrement}
						className={fieldCss.actionButton}
						disabled={(value || 0) >= max || disabled}
					/>
					<Button
						appearance="subtle"
						icon={<ChevronDownIcon width={14} height={14} />}
						onClick={handleDecrement}
						className={fieldCss.actionButton}
						disabled={(value || 0) <= min || disabled}
					/>
				</>
			}
			topLabel={!!fieldValue || !!value?.toString() || hasFocus}
			className={className}
		>
			<input
				id={id}
				disabled={disabled}
				className={cls(
					css.input,
					inputCss.input,
					!!hasError && inputCss.hasError,
					hasFocus && inputCss.hasFocus,
					disabled && inputCss.disabled,
				)}
				{...(inputProps as any)}
				onChange={handleFieldChange}
				onBlur={handleBlur}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={ref}
				onWheel={handleScroll}
				value={fieldValue || value?.toString() || ''}
				type={'tel'}
				autoComplete={'off'}
			/>
		</Field>
	);
};
